import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { Lock, Eye, EyeOff, Check, User, Trash2, AlertTriangle, FileText, Download, Mail, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showClearDataDialog, setShowClearDataDialog] = useState(false);
  const [clearDataConfirmation, setClearDataConfirmation] = useState('');
  const [clearingData, setClearingData] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchEmailTemplates();
  }, []);

  const fetchEmailTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/corporate/email-templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEmailTemplates(data);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const downloadCorporatePackage = () => {
    window.open(`${API_URL}/api/corporate/volunteer-package/download`, '_blank');
    toast.success('Downloading Corporate Volunteer Package PDF');
  };

  const viewTemplate = (templateId, template) => {
    setSelectedTemplate({ id: templateId, ...template });
    setShowTemplateDialog(true);
  };

  const copyTemplateToClipboard = () => {
    if (!selectedTemplate) return;
    const text = `Subject: ${selectedTemplate.subject}\n\n${selectedTemplate.body}`;
    navigator.clipboard.writeText(text);
    toast.success('Template copied to clipboard!');
  };

  const downloadTemplateAsText = () => {
    if (!selectedTemplate) return;
    const text = `Subject: ${selectedTemplate.subject}\n\n${selectedTemplate.body}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTemplate.name.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const handleClearAllData = async () => {
    if (clearDataConfirmation !== 'DELETE ALL DATA') {
      toast.error('Please type "DELETE ALL DATA" to confirm');
      return;
    }

    setClearingData(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/clear-all-data`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to clear data');
      }

      const result = await response.json();
      toast.success('All application data cleared successfully');
      console.log('Clear data results:', result.details);
      setShowClearDataDialog(false);
      setClearDataConfirmation('');
    } catch (error) {
      toast.error(error.message || 'Failed to clear data');
    } finally {
      setClearingData(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change password');
      }
      
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Settings
          </h1>
          <p className="text-slate-500">Manage your account settings</p>
        </div>

        <div className="grid gap-6 max-w-2xl">
          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1a3a5c]">
                <User className="w-5 h-5" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Username</p>
                  <p className="font-medium text-slate-900">{user?.username || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Name</p>
                  <p className="font-medium text-slate-900">{user?.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">{user?.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Role</p>
                  <p className="font-medium text-slate-900 capitalize">{user?.role || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1a3a5c]">
                <Lock className="w-5 h-5" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Must be at least 8 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                  data-testid="change-password-btn"
                >
                  {loading ? (
                    'Changing...'
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Documents & Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1a3a5c]">
                <FileText className="w-5 h-5" />
                Documents & Templates
              </CardTitle>
              <CardDescription>
                Download and review all documents and email templates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Documents Section */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Documents</Label>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#1a3a5c]" />
                    <div>
                      <p className="font-medium text-sm">Corporate Volunteer Package</p>
                      <p className="text-xs text-slate-500">PDF document for corporate partners</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={downloadCorporatePackage}
                    data-testid="download-corp-package-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>

              {/* Email Templates Section */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Email Templates</Label>
                <div className="space-y-2">
                  {Object.entries(emailTemplates).map(([id, template]) => (
                    <div key={id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-[#1a3a5c]" />
                        <div>
                          <p className="font-medium text-sm">{template.name}</p>
                          <p className="text-xs text-slate-500 truncate max-w-xs">{template.subject}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => viewTemplate(id, template)}
                        data-testid={`view-template-${id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone - Clear All Data */}
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Danger Zone
              </CardTitle>
              <CardDescription>
                Irreversible actions that affect all application data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-semibold text-red-800 mb-2">Clear All Application Data</h4>
                <p className="text-sm text-red-700 mb-4">
                  This will permanently delete all volunteers, corporate partners, events, hours, and reset all metrics to zero. 
                  Admin user accounts will be preserved.
                </p>
                <Button 
                  variant="destructive"
                  onClick={() => setShowClearDataDialog(true)}
                  data-testid="clear-all-data-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Data
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clear All Data Confirmation Dialog */}
        <Dialog open={showClearDataDialog} onOpenChange={setShowClearDataDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Clear All Application Data
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium mb-2">
                  This action will permanently delete:
                </p>
                <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                  <li>All volunteer applications and records</li>
                  <li>All corporate partner requests</li>
                  <li>All events and corporate events</li>
                  <li>All logged volunteer hours</li>
                  <li>All impact metrics (reset to zero)</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-delete" className="text-sm font-medium">
                  Type <span className="font-mono bg-slate-100 px-1 rounded">DELETE ALL DATA</span> to confirm:
                </Label>
                <Input
                  id="confirm-delete"
                  value={clearDataConfirmation}
                  onChange={(e) => setClearDataConfirmation(e.target.value)}
                  placeholder="DELETE ALL DATA"
                  className="font-mono"
                  data-testid="confirm-clear-input"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowClearDataDialog(false);
                    setClearDataConfirmation('');
                  }} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleClearAllData}
                  disabled={clearDataConfirmation !== 'DELETE ALL DATA' || clearingData}
                  className="flex-1"
                  data-testid="confirm-clear-btn"
                >
                  {clearingData ? 'Clearing...' : 'Clear All Data'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Template Viewer Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#1a3a5c]" />
                {selectedTemplate?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Subject</Label>
                <div className="p-3 bg-slate-50 rounded border text-sm font-medium">
                  {selectedTemplate?.subject}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Body</Label>
                <div className="p-3 bg-slate-50 rounded border text-sm whitespace-pre-wrap max-h-80 overflow-y-auto">
                  {selectedTemplate?.body}
                </div>
              </div>
              <p className="text-xs text-slate-500">
                <strong>Note:</strong> Variables like {'{contact_name}'}, {'{company_name}'}, etc. will be replaced with actual values when sending.
              </p>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={copyTemplateToClipboard}
                  className="flex-1"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy to Clipboard
                </Button>
                <Button 
                  variant="outline" 
                  onClick={downloadTemplateAsText}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download as Text
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
